import React, { useEffect, useState } from "react";
import "./ScheduleTable.scss";
import BlockModal from "./BlockModal";
import Loader from "../../../components/Loader/Loader";
import UnblockModal from "./UnblockModal";

const ScheduleTable = () => {
  const [schedule, setSchedule] = useState({});
  const [dates, setDates] = useState([]);
  const [sport, setSport] = useState(""); // Initialize as empty
  const [availableSports, setAvailableSports] = useState([]); // State for available sports
  const [modalData, setModalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isUnBlockModalOpen, setIsUnBlockModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch initial data (facility details and schedule) in parallel
  const fetchInitialData = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("access_token");

    try {
      // Prepare API calls
      const facilityPromise = fetch(
        "https://ballersapi.ballerslife.co/api/v1/facility_admin/sports_facility/available_sports",
        {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Run facility API call
      const facilityResponse = await facilityPromise;

      // Handle facility response
      if (!facilityResponse.ok) {
        throw new Error(
          `Facility API error: ${facilityResponse.status} ${facilityResponse.statusText}`
        );
      }
      const facilityData = await facilityResponse.json();
      const sports = facilityData || [];
      setAvailableSports(sports);

      // Set the default sport to the first available sport
      if (sports.length > 0) {
        setSport(sports[0]);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error.message);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch schedule for the selected sport
  const fetchSchedule = async () => {
    if (!sport) return; // Prevent fetching if no sport is selected
    const token = localStorage.getItem("access_token");
    setLoading(true);
    setError(null);
    try {
      const url = `https://ballersapi.ballerslife.co/api/v1/facility_admin/datewise_facility_bookings?sport=${sport}`;
      const response = await fetch(url, {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setSchedule(data.datewise_slots);
      const apiDates = Object.keys(data.datewise_slots || {});
      setDates(apiDates);
    } catch (error) {
      console.error("Error fetching schedule:", error.message);
      setError("Failed to load schedule. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial data on mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch schedule when sport changes
  useEffect(() => {
    fetchSchedule();
  }, [sport]);

  const getSlotTextColor = (date, time) => {
    const slots = schedule[date]?.[sport] || {};
    const slot = slots[time];

    if (!slot) return "green";
    const courtsAvailable =
      slot.courts_available || slot.courts_available_at_facility || 0;
    const courtsBooked = slot.courts_booked || 0;

    if (courtsBooked === courtsAvailable) return "red";
    if (courtsBooked < courtsAvailable) return "yellow";
    return "white";
  };

  const fetchSlotDetails = async (date, time) => {
    const token = localStorage.getItem("access_token");
    try {
      const url = `https://ballersapi.ballerslife.co/api/v1/facility_admin/get_slot_info_details?date=${date}&time=${time}&sport=${sport}`;
      const response = await fetch(url, {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setModalData(data);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching slot details:", error.message);
      setError("Failed to load slot details.");
    }
  };

  const closeSlotModal = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  const openBlockModal = () => {
    setIsBlockModalOpen(true);
  };

  const closeBlockModal = () => {
    setIsBlockModalOpen(false);
  };

  const openUnBlockModal = () => {
    setIsUnBlockModalOpen(true);
  };

  const closeUnBlockModal = () => {
    setIsUnBlockModalOpen(false);
  };

  const handleBlockSlot = async (blockData) => {
    const token = localStorage.getItem("access_token");
    const formattedDate = blockData.date;
    const formattedTime = blockData.time;

    try {
      const response = await fetch(
        `https://ballersapi.ballerslife.co/api/v1/facility_admin/block_slot?sport=${blockData.sport}&block_date=${formattedDate}&blocked_time=${formattedTime}&duration=${blockData.duration}&court_req=${blockData.noOfCourts}`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (response.ok) {
        closeBlockModal();
        fetchSchedule();
      } else {
        console.error("Error blocking slot:", data);
        setError("Failed to block slot.");
      }
    } catch (error) {
      console.error("Error blocking slot:", error.message);
      setError("Failed to block slot.");
    }
  };

  const times = Array.from({ length: 18 }, (_, i) => {
    const hour = 6 + i;
    return `${hour.toString().padStart(2, "0")}:00`;
  });

  return (
    <>
      {loading ? (
        <Loader message="Loading sports and schedule..." />
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="schedule-table">
          <div className="table-header-controls">
            <button className="block-btn" onClick={openUnBlockModal}>
              + Unblock Slot
            </button>
            <button className="block-btn" onClick={openBlockModal}>
              + Block Slot
            </button>
            <select
              className="sport-dropdown"
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              disabled={availableSports.length === 0}
            >
              {availableSports.map((sportItem) => (
                <option key={sportItem} value={sportItem}>
                  {sportItem.charAt(0).toUpperCase() + sportItem.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="table-header">
            {dates.map((date) => (
              <div key={date} className="header-cell">
                {new Date(date).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </div>
            ))}
          </div>
          <div className="table-body">
            {times.map((time) => (
              <div key={time} className="time-row">
                {dates.map((date) => (
                  <div
                    key={`${date}-${time}`}
                    className="slot-cell"
                    style={{ color: getSlotTextColor(date, time) }}
                    onClick={() => fetchSlotDetails(date, time)}
                  >
                    {time}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {isModalOpen && modalData && (
            <div className="modal">
              <div className="modal-content-facility">
                <button className="close-btn" onClick={closeSlotModal}>
                  ×
                </button>
                <p className="modal-paragraph">
                  <strong style={{ color: "#ffb000" }}>DATE :</strong>{" "}
                  {modalData.date}
                </p>
                <p className="modal-paragraph">
                  <strong style={{ color: "#ffb000" }}>TIME :</strong>{" "}
                  {modalData.time} - {parseTimeRange(modalData.time)}
                </p>
                <p className="modal-paragraph">
                  <strong style={{ color: "#ffb000" }}>
                    Booked by Ballers :
                  </strong>{" "}
                  {modalData["Booked by Ballers"]}
                </p>
                <p className="modal-paragraph">
                  <strong style={{ color: "#ffb000" }}>
                    Available court :
                  </strong>{" "}
                  {modalData.available_courts}
                </p>
                <p className="modal-paragraph">
                  <strong style={{ color: "#ffb000" }}>
                    Blocked by Facility :
                  </strong>{" "}
                  {modalData["Blocked by Facility"]}
                </p>
              </div>
            </div>
          )}

          {isBlockModalOpen && (
            <BlockModal
              onClose={closeBlockModal}
              onBlock={handleBlockSlot}
              initialSport={sport}
              availableSports={availableSports}
            />
          )}
          {isUnBlockModalOpen && (
            <UnblockModal
              onClose={closeUnBlockModal}
              initialSport={sport}
              availableSports={availableSports}
              fetchInitialData={fetchInitialData}
            />
          )}
        </div>
      )}
    </>
  );
};

// Helper function to parse time range (e.g., "09:00" to "09:00 - 10:00")
const parseTimeRange = (time) => {
  const [hour] = time.split(":");
  const nextHour = (parseInt(hour) + 1).toString().padStart(2, "0");
  return `${nextHour}:00`;
};

export default ScheduleTable;
